import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { getCustomerGroups } from "../../services/customerGroupService";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Avatar,
  Divider,
  CardActions,
  Button,
  Badge,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";

const CustomerGroupList = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await getCustomerGroups();
      console.log("Customer Groups API Response:", response);

      if (response.success && Array.isArray(response.data)) {
        setGroups(response.data);
      } else {
        toast.error("Unexpected response format from the server.");
        setGroups([]);
      }
    } catch (error) {
      console.error("Error fetching customer groups:", error.message);
      toast.error(
        error.response?.data?.message || "Failed to fetch customer groups"
      );
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = (group_id, group_name) => {
    navigate("/customer", { state: { group_id, group_name } });
  };

  return (
    <Container maxWidth="sm" sx={{ py: 0, mt: 4 }}>
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{ fontWeight: 600, color: "primary.main" }}
      >
        👥 Customer Groups
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : groups.length === 0 ? (
        <Typography align="center" color="text.secondary">
          No customer groups found.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
          {groups.map((group) => (
            <Card
              key={group.group_id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                boxShadow: 1,
                position: "relative",
                px: 2,
                py: 1.5,
                transition: "0.3s",
                "&:hover": {
                  boxShadow: 4,
                  borderColor: "primary.light",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                  <GroupIcon />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {group.group_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.description || "No description"}
                  </Typography>
                </Box>

                {/* Group Count Badge */}
                <Badge
                  badgeContent={group.customer_count}
                  color={group.customer_count > 20 ? "primary" : "secondary"}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.75rem",
                      padding: "6px 8px",
                      fontWeight: "bold",
                    },
                  }}
                />
              </Box>

              <Divider sx={{ mb: 1 }} />

              <CardActions sx={{ p: 0 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    handleGroupClick(group.group_id, group.group_name)
                  }
                  sx={{ ml: "auto", fontWeight: 500 }}
                >
                  View Customers →
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default CustomerGroupList;
